import React, { Dispatch, ForwardedRef, forwardRef, PropsWithChildren, SetStateAction, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatNumber } from '@progress/kendo-intl';
import { classNames } from '@progress/kendo-react-common';
import { useTableKeyboardNavigation } from '@progress/kendo-react-data-tools';
import { GRID_COL_INDEX_ATTRIBUTE, GridCustomCellProps } from '@progress/kendo-react-grid';
import { Tooltip } from '@progress/kendo-react-tooltip';
import cn from 'clsx';
import { ScoreDirection } from '@components-clean/central-monitoring/panels/panel-constants';
import { bankersRound } from '@components-clean/core/charts/shared/number-utils';
import { RAGColourIndex } from '@components-clean/core/charts/shared/utils';
import styles from '@components-clean/core/grid/custom-cell/style.module.scss';
import { FormatCellProps, MultiValueCell, MultiValueCellProps } from '@components-clean/core/grid/multi-value-cell';
import ragStyles from '@components-clean/core/grid/multi-value-cell/style.module.scss';
import { DateTimeCellProps, PanelWeightCustomCellProps } from '@components-clean/core/grid-fe/grid.interface';
import { HC_RAG_NAMES } from '@interfaces/opra-chart.interface';
import { NonRAGDataItem } from '@pages-clean/dummy-page/dummy-page.data';

import {
    convertBooleanToYesNo,
    stringDateWithoutTimeFromUtcNonNullable,
    stringDateWithTimeFromUtcNonNullable,
} from '@utils/convertor';

export const DateTimeCell = (formatProp?: string) =>
    function inner({ tdProps, dataItem, field, ...rest }: GridCustomCellProps) {
        const navigationAttributes = useTableKeyboardNavigation(rest.id);

        const value = field
            ? stringDateWithTimeFromUtcNonNullable(
                  field.split('.').reduce((obj, key) => obj?.[key], dataItem) as Date,
                  formatProp,
              )
            : '';

        return (
            <td
                {...tdProps}
                className={cn(rest.className || '', tdProps?.className)}
                colSpan={rest.colSpan}
                role="gridcell"
                aria-colindex={rest.ariaColumnIndex}
                aria-selected={rest.isSelected}
                {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                {...navigationAttributes}
            >
                <div className="ellipsis-overflow">{value}</div>
            </td>
        );
    };

export const DateCell = ({ className, dataItem, style, field, title, icon, ...rest }: DateTimeCellProps) => {
    const navigationAttributes = useTableKeyboardNavigation(rest.id);

    const dataItemField = field ? (dataItem[field] as string) : '';
    const content = icon && (
        <div title={title} className="k-d-flex k-justify-content-center k-align-items-center k-gap-xs">
            <FontAwesomeIcon icon={icon} style={{ pointerEvents: 'none' }} />
        </div>
    );

    return (
        <td
            {...rest.tdProps}
            className={cn(className || '', rest.tdProps?.className)}
            colSpan={rest.colSpan}
            role="gridcell"
            aria-colindex={rest.ariaColumnIndex}
            aria-selected={rest.isSelected}
            {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
            {...navigationAttributes}
        >
            <div className="k-d-flex k-inline-flex k-flex-row k-align-items-center k-gap-sm">
                <span>{stringDateWithoutTimeFromUtcNonNullable(dataItemField, 'DD-MMM-YYYY')}</span>

                {icon && title && (
                    <span className="k-ml-auto">
                        <Tooltip position="bottom" style={{ maxWidth: '125px' }}>
                            {content}
                        </Tooltip>
                    </span>
                )}
            </div>
        </td>
    );
};

export const PartialDateCell = ({ className, dataItem, style, field, ...rest }: GridCustomCellProps) => {
    const navigationAttributes = useTableKeyboardNavigation(rest.id);

    const dataItemField = field ? (dataItem[field] as string) : '';

    return (
        <td
            {...rest.tdProps}
            className={cn(className || '', rest.tdProps?.className)}
            colSpan={rest.colSpan}
            role="gridcell"
            aria-colindex={rest.ariaColumnIndex}
            aria-selected={rest.isSelected}
            {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
            {...navigationAttributes}
        >
            <div className="ellipsis-overflow">{dataItemField}</div>
        </td>
    );
};

export const YesNoCell = ({ className, dataItem, style, field, ...rest }: GridCustomCellProps) => {
    const navigationAttributes = useTableKeyboardNavigation(rest.id);

    const fieldValue: unknown = field ? dataItem[field] : '';
    const currentBoolValue =
        typeof fieldValue === 'string' || typeof fieldValue === 'boolean' ? convertBooleanToYesNo(fieldValue) : '';

    return (
        <td
            {...rest.tdProps}
            className={cn(className || '', rest.tdProps?.className)}
            colSpan={rest.colSpan}
            role="gridcell"
            aria-colindex={rest.ariaColumnIndex}
            aria-selected={rest.isSelected}
            {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
            {...navigationAttributes}
        >
            {currentBoolValue}
        </td>
    );
};

export const CellWithChildren = ({ tdProps, dataItem, children, ...rest }: GridCustomCellProps & PropsWithChildren) => {
    const navigationAttributes = useTableKeyboardNavigation(rest.id);
    return (
        <td
            {...tdProps}
            className={cn(rest.className || '', tdProps?.className)}
            colSpan={rest.colSpan}
            role="gridcell"
            aria-colindex={rest.ariaColumnIndex}
            aria-selected={rest.isSelected}
            {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
            {...navigationAttributes}
        >
            {children}
        </td>
    );
};

export const DragHandleCell = (props: GridCustomCellProps) => {
    const { tdProps } = props;
    return (
        <td
            {...tdProps}
            style={{
                touchAction: 'none',
                textAlign: 'center',
            }}
        >
            <span
                style={{
                    cursor: 'move',
                    width: 16,
                }}
                data-drag-handle="true"
            >
                <FontAwesomeIcon
                    style={{
                        pointerEvents: 'none',
                    }}
                    icon={['fas', 'grip-dots-vertical']}
                    className="fa-lg"
                />
            </span>
        </td>
    );
};

export const TooltipContentTemplate = forwardRef(
    (
        {
            title,
            setShowTooltip,
            handler,
        }: {
            title: string;
            setShowTooltip: Dispatch<SetStateAction<boolean>>;
            handler: NodeJS.Timeout | null;
        },
        ref: ForwardedRef<HTMLDivElement>,
    ) => {
        return (
            <div
                ref={ref}
                style={{ maxWidth: 300, maxHeight: 400, overflow: 'auto' }}
                onMouseEnter={() => {
                    if (handler) clearTimeout(handler);
                }}
                onMouseLeave={() => setShowTooltip(false)}
            >
                {title}
            </div>
        );
    },
);

export const MaxLengthCell = (max: number) =>
    function inner({ className, dataItem, style, field, tdProps, ...rest }: GridCustomCellProps) {
        const navigationAttributes = useTableKeyboardNavigation(rest.id);

        const fieldValue = field ? (field.split('.').reduce((obj, key) => obj?.[key], dataItem) as string) : '';

        const [showTooltip, setShowTooltip] = React.useState(false);
        const handler = useRef<ReturnType<typeof setTimeout> | null>(null);
        const tooltipElRef = useRef<HTMLDivElement>(null);
        const targetElRef = useRef(null);

        return (
            <td
                {...tdProps}
                onMouseOver={() => {
                    if (handler.current) clearTimeout(handler.current);
                    setShowTooltip(true);
                }}
                onFocus={() => {}}
                onMouseLeave={() => {
                    if (
                        tooltipElRef.current &&
                        tooltipElRef.current.scrollHeight <= tooltipElRef.current.clientHeight
                    ) {
                        setShowTooltip(false);
                    } else
                        handler.current = setTimeout(() => {
                            setShowTooltip(false);
                        }, 200);
                }}
                className={classNames(['max-length-cell', className || ''])}
                colSpan={rest.colSpan}
                role="gridcell"
                aria-colindex={rest.ariaColumnIndex}
                aria-selected={rest.isSelected}
                {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
                {...navigationAttributes}
            >
                {fieldValue ? (
                    React.isValidElement(fieldValue) || (fieldValue && fieldValue.length <= max) ? (
                        fieldValue
                    ) : (
                        <Tooltip
                            anchorElement="target"
                            position="auto"
                            open={showTooltip}
                            content={() => (
                                <TooltipContentTemplate
                                    ref={tooltipElRef}
                                    title={fieldValue}
                                    setShowTooltip={setShowTooltip}
                                    handler={handler.current}
                                />
                            )}
                        >
                            <span ref={targetElRef} title={fieldValue}>
                                {fieldValue.substr(0, max)}...
                            </span>
                        </Tooltip>
                    )
                ) : (
                    ''
                )}
            </td>
        );
    };

export const getHighlightClass = (color: string) => {
    switch (color) {
        case 'Red':
        case 'RED':
            return ragStyles.highlightedRed;
        case 'Green':
        case 'GREEN':
            return ragStyles.highlightedGreen;
        case 'Amber':
        case 'AMBER':
            return ragStyles.highlightedAmber;
        case 'Purple':
            return ragStyles.highlightedPurple;
        default:
            return '';
    }
};

export const EvaluationScoreCell = ({
    tdProps,
    dataItem,
    field,
    numberFormat,
    ...rest
}: GridCustomCellProps & MultiValueCellProps & FormatCellProps) => {
    const navigationAttributes = useTableKeyboardNavigation(rest.id);
    const fieldValue: unknown = field ? field.split('.').reduce((a, b) => a[b] ?? '', dataItem) : '';

    let colour;
    let score;
    if (fieldValue !== undefined) {
        if (fieldValue && typeof fieldValue === 'object' && 'colour' in fieldValue && fieldValue.colour)
            colour = fieldValue.colour as string;
        if (fieldValue && typeof fieldValue === 'object' && 'score' in fieldValue && fieldValue.score)
            score = +(fieldValue.score as string);
    }

    return (
        <td
            {...tdProps}
            className={cn(rest.className || '', tdProps?.className)}
            colSpan={rest.colSpan}
            role="gridcell"
            aria-colindex={rest.ariaColumnIndex}
            aria-selected={rest.isSelected}
            {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
            {...navigationAttributes}
        >
            <div className="k-display-flex k-align-items-center k-justify-content-center">
                <div className={cn(`${score ? ragStyles.evaluationValueContainer : ''}`)}>
                    <span className={cn(getHighlightClass(colour ?? 'None'))} style={{ textAlign: 'center' }}>
                        {!fieldValue || !score
                            ? ''
                            : numberFormat
                              ? formatNumber(score, numberFormat) || '-'
                              : bankersRound(score, 2) || '-'}
                    </span>
                </div>
            </div>
        </td>
    );
};

export const MultiValueEvaluationCell = (
    props: GridCustomCellProps &
        MultiValueCellProps &
        FormatCellProps & {
            dataItem: NonRAGDataItem;
            showBlankText?: boolean;
        },
) => {
    const { dataItem, field, showBlankText } = props;
    const fieldValue: unknown = field ? field.split('.').reduce((a, b) => a[b] ?? '', dataItem) : '';

    let valueBefore = 0;
    let valueAfter = 0;
    let changeDirection = ScoreDirection.Matching;
    let movement = '';
    let colourBefore = '';
    let colourAfter = '';
    if (fieldValue !== undefined) {
        if (fieldValue && typeof fieldValue === 'object' && 'score' in fieldValue)
            valueAfter = fieldValue.score as number;
        if (fieldValue && typeof fieldValue === 'object' && 'prevScore' in fieldValue)
            valueBefore = fieldValue.prevScore as number;
        if (fieldValue && typeof fieldValue === 'object' && 'movement' in fieldValue)
            movement = fieldValue.movement as string;
        if (fieldValue && typeof fieldValue === 'object' && 'colour' in fieldValue)
            colourAfter = RAGColourIndex(fieldValue.colour as string).toString();
        if (fieldValue && typeof fieldValue === 'object' && 'prevColour' in fieldValue)
            colourBefore = RAGColourIndex(fieldValue.prevColour as string).toString();

        changeDirection =
            movement && movement !== ''
                ? movement === 'RISING' || movement === 'Increased'
                    ? ScoreDirection.Upper
                    : movement === 'FALLING' || movement === 'Decreased'
                      ? ScoreDirection.Lower
                      : changeDirection
                : changeDirection;
    }
    const item = {
        valueAfter,
        valueBefore,
        colourAfter,
        colourBefore,
        changeDirection,
    };
    return <MultiValueCell {...props} dataItem={item} showBlankText={showBlankText} width={41} />;
};

export const RagValueCell = ({
    tdProps,
    dataItem,
    field,
    numberFormat,
    ...rest
}: GridCustomCellProps & MultiValueCellProps & FormatCellProps) => {
    const navigationAttributes = useTableKeyboardNavigation(rest.id);
    const fieldValue: unknown = field ? field.split('.').reduce((a, b) => a[b] ?? '', dataItem) : '';

    let colour;
    let rateOrScore;
    if (fieldValue !== undefined) {
        if (fieldValue && typeof fieldValue === 'object' && 'colour' in fieldValue && fieldValue.colour)
            colour = +(fieldValue.colour as string);
        if (fieldValue && typeof fieldValue === 'object' && 'rateOrScore' in fieldValue && fieldValue.rateOrScore)
            rateOrScore = +(fieldValue.rateOrScore as string);
    }

    return (
        <td
            {...tdProps}
            className={cn(rest.className || '', tdProps?.className)}
            style={rest.style}
            colSpan={rest.colSpan}
            role="gridcell"
            aria-colindex={rest.ariaColumnIndex}
            aria-selected={rest.isSelected}
            {...{ [GRID_COL_INDEX_ATTRIBUTE]: rest.columnIndex }}
            {...navigationAttributes}
        >
            <div className="k-display-flex k-align-items-center k-justify-content-center">
                <div className={cn(`${ragStyles.kriRagValueContainer}`)}>
                    <span
                        className={cn(getHighlightClass(HC_RAG_NAMES[colour ?? 0]))}
                        style={{ textAlign: !fieldValue || !rateOrScore ? 'center' : 'right' }}
                    >
                        {!fieldValue || !rateOrScore
                            ? 'N/A'
                            : numberFormat
                              ? formatNumber(rateOrScore, numberFormat) || '-'
                              : bankersRound(rateOrScore, 2) || '-'}
                    </span>
                </div>
            </div>
        </td>
    );
};

export const MultiValueDataCell = (
    props: GridCustomCellProps &
        MultiValueCellProps &
        FormatCellProps & {
            dataItem: NonRAGDataItem;
            showBlankText?: boolean;
        },
) => {
    const { dataItem, field, showBlankText } = props;
    const fieldValue: unknown = field ? field.split('.').reduce((a, b) => a[b] ?? '', dataItem) : '';

    let valueBefore = 0;
    let valueAfter = 0;
    let changeDirection = ScoreDirection.Matching;
    let colourBefore = '';
    let colourAfter = '';
    if (fieldValue !== undefined) {
        if (fieldValue && typeof fieldValue === 'object' && 'valueInFocus' in fieldValue)
            valueAfter = fieldValue.valueInFocus as number;
        if (fieldValue && typeof fieldValue === 'object' && 'comparisonValue' in fieldValue)
            valueBefore = fieldValue.comparisonValue as number;
        if (fieldValue && typeof fieldValue === 'object' && 'changeDirection' in fieldValue)
            changeDirection = fieldValue.changeDirection as number;
        if (fieldValue && typeof fieldValue === 'object' && 'colourInFocus' in fieldValue)
            colourAfter = fieldValue.colourInFocus as string;
        if (fieldValue && typeof fieldValue === 'object' && 'comparisonColour' in fieldValue)
            colourBefore = fieldValue.comparisonColour as string;
    }
    const item = {
        valueAfter,
        valueBefore,
        colourAfter,
        colourBefore,
        changeDirection,
    };
    return <MultiValueCell {...props} dataItem={item} showBlankText={showBlankText} />;
};

export const PanelWeightCell = (props: PanelWeightCustomCellProps) => {
    const { dataItem, children, ...rest } = props;

    if (!dataItem.panelWeightDetails?.includeInSiteScoring || !dataItem.panelWeightDetails?.weight) {
        return (
            <CellWithChildren dataItem={dataItem} {...rest}>
                {children}
            </CellWithChildren>
        );
    }
    return (
        <CellWithChildren dataItem={dataItem} {...rest}>
            <Tooltip position="top" tooltipClassName={styles.lineBreak}>
                <div
                    className="k-d-flex k-justify-content-center k-align-items-center k-gap-xs"
                    title={`Included in Scoring\nWeight: ${dataItem.panelWeightDetails.weight}`}
                >
                    <FontAwesomeIcon icon={['fas', 'calculator']} style={{ pointerEvents: 'none' }} />
                </div>
            </Tooltip>
        </CellWithChildren>
    );
};
